import React, {useCallback, useEffect, useState} from 'react';
import styles from "./AttachEventsModal.module.scss";
import {Button, Checkbox, Input, Modal, Paginator, Preloader, Status, useToast} from "uikit";
import SearchIcon from "@/assets/images/icons/16/search.svg";
import CrossIcon from "uikit/src/assets/icons/16/cross.svg";
import {
    AttachEventsModalSchema
} from "@/views/Admin/Events/Event/Promocodes/AttachEventsModal/AttachEventsModal.schema";
import {
    AttachEventsModalPropsInterface
} from "@/views/Admin/Events/Event/Promocodes/AttachEventsModal/AttachEventsModal.props.interface";
import {
    AttachEventsModalInterface
} from "@/views/Admin/Events/Event/Promocodes/AttachEventsModal/AttachEventsModal.interface";
import {PromocodesService} from "@/services/PromocodesService/PromocodesService";
import {EventBasesService} from "@/services/EventBasesService/EventBasesService";
import {EventBasesServiceInterface} from "@/services/EventBasesService/EventBasesService.interface";
import {
    classifyEvents,
    determineLinkActions,
    getEventState,
    handlePrepareParams,
    prepareMapCollections,
    processPromocodeLinkActions
} from "@/views/Admin/Events/Event/Promocodes/AttachEventsModal/AttachEventsModal.internal";
import {PromocodeInterface} from "@/views/Admin/Events/Event/Promocodes/Promocode/Promocode.interface";
import {TicketInterface} from "@/types/Ticket.interface";
import {useQueryRouter} from "@/hooks/useQueryRouter/useQueryRouter";
import {useTranslation} from "react-i18next";
import {EventStateSchema} from "@/schema/EventState.schema";

const AttachEventsModal = ({data, onChange, active, onActive}: AttachEventsModalPropsInterface) => {
    const [content, setContent] = useState<AttachEventsModalInterface>({...AttachEventsModalSchema});
    const [isLoading, setLoading] = useState({
        event: false,
        connecting: false
    });
    const {meta} = content;

    const {currentPage, perPage, lastPage, sizes} = meta;
    const {query} = useQueryRouter({page: {number: currentPage, size: perPage}});
    const toast = useToast();
    const {t} = useTranslation('common');

    useEffect(() => {
        if (!active) return;

        handleGetEventBases();
    }, [active, currentPage, perPage]);

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setContent(prev => ({...prev, search: {...prev.search, isActive: true}}));
        handleGetEventBases(content.search.value);
    }

    const handleSearchReset = () => {
        setContent(prev => ({...prev, search: AttachEventsModalSchema.search}));
        handleGetEventBases();
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setContent(prev => ({...prev, search: {value: e.target.value, isActive: false}}));
    }

    const handleGetEventBases = async (searchQuery?: string) => {
        const relatedEventIds = new Set(data.relatedEvents.map(e => e.eventId));
        const page = {number: currentPage, size: perPage};
        const filters = {
            include: 'tickets',
            q: searchQuery ? searchQuery : null,
            page
        };
        const params = handlePrepareParams(filters);

        setLoading(prev => ({...prev, event: true}));

        try {
            const response = await EventBasesService.GetList(params);
            const related = parseRelatedEvents(response.data as EventBasesServiceInterface[]);
            const parsedEvents = classifyEvents(response.data as EventBasesServiceInterface[], data.initialPromocodeEvent.map(event => Number(event.eventId)), relatedEventIds);

            setContent(prev => (
                {
                    ...prev,
                    baseEvents: [
                        ...parsedEvents.initial,
                        ...parsedEvents.related,
                        ...parsedEvents.regular
                    ],
                    selectedEvents: Object.keys(related).length ? {...prev.selectedEvents, ...related} : {...prev.selectedEvents},
                    meta: {...meta, ...response.meta}
                })
            );
        } catch (err) {
            toast({type: 'danger', message: err.message || `${t('has_occurred')}. ${t('try_again_later')}`});
        } finally {
            setLoading(prev => ({...prev, event: false}));
        }
    }

    const parseRelatedEvents = useCallback((baseEvents: EventBasesServiceInterface[]) => {
        const eventsMap = new Map(baseEvents.map(e => [e.eventId, e]));
        const related: Record<number, Record<string | number, boolean>> = {};

        for (const relatedEvent of data.relatedEvents) {
            if (eventsMap.has(relatedEvent.eventId)) {
                related[relatedEvent.eventId] = Object.fromEntries(
                    relatedEvent.tickets.map((ticket: TicketInterface) => [ticket.ticketId, true])
                );
            }
        }

        return related;
    }, [content.baseEvents, data.relatedEvents]);

    const handleSelectEvent = (event: EventBasesServiceInterface, isChecked: boolean) => {
        const ticketsIds = event.tickets.map((ticket: TicketInterface) => ticket.ticketId);

        setContent(prev => {
            const currentEventSelection = prev.selectedEvents[event.eventId] || {};
            const allTicketsSelected = ticketsIds.every(id => currentEventSelection[id]);

            return {
                ...prev,
                selectedEvents: {
                    ...prev.selectedEvents,
                    [event.eventId]: (!allTicketsSelected || isChecked)
                        ? Object.fromEntries(ticketsIds.map(id => [id, true]))
                        : Object.fromEntries(ticketsIds.map(id => [id, false]))
                }
            };
        });
    };

    const handleSelectTicket = (eventId: number, ticketId: number, isChecked: boolean) => {
        setContent(prev => ({
            ...prev,
            selectedEvents: {
                ...prev.selectedEvents,
                [eventId]: {
                    ...prev.selectedEvents[eventId],
                    [ticketId]: isChecked
                }
            }
        }));
    }

    const handleActive = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()

        content.baseEvents.forEach(event => {
            setContent(prev => ({
                ...prev,
                selectedEvents: {
                    ...prev.selectedEvents,
                    [event.eventId]: Object.fromEntries(event.tickets.map((ticket: TicketInterface) => [ticket.ticketId, true]))
                }
            }))
        })
    }

    const handleDrop = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()

        setContent(prev => ({
            ...prev,
            selectedEvents: Object.fromEntries(
                Object.entries(prev.selectedEvents).map(([eventId, tickets]) => [
                    eventId,
                    tickets && Object.fromEntries(
                        Object.keys(tickets).map(k => [k, false])
                    )
                ]))
        }));
    }

    const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()

        setContent(prev => ({...prev, selectedEvents: {}, search: AttachEventsModalSchema.search}))
        onActive(false)
    }

    const prepareLinkActions = () => {
        const related = parseRelatedEvents(content.baseEvents);
        const {relatedMap, baseEventsMap} = prepareMapCollections(content.baseEvents, related)

        return Object.entries(content.selectedEvents)
            .map(([eventId, tickets]) => {
                const baseEvent = baseEventsMap.get(Number(eventId));
                if (!baseEvent) return null;

                const {
                    isSelectedFullEvent,
                    isRelatedEventSelected,
                    relatedTickets,
                    allTicketIds
                } = getEventState(baseEvent, tickets, relatedMap, Number(eventId));

                return determineLinkActions({
                    eventId: Number(eventId),
                    tickets: tickets,
                    isSelectedFullEvent,
                    isRelatedEventSelected,
                    relatedTickets,
                    allTicketIds,
                    initialPromocodeEventId: data.initialPromocodeEvent.map(event => Number(event.eventId))
                });
            })
            .flat()
            .filter(Boolean);
    };

    const handleUpdatePromocode = async () => {
        // @ts-ignore
        const page = {number: query?.page?.number || currentPage, size: query?.page?.size || perPage}

        try {
            const promocodesList = await PromocodesService.GetList({
                filter: {},
                page: page,
                include: ['event', 'event.tickets', 'foreign-events', 'foreign-events.tickets', 'tickets']
            }) as { data: PromocodeInterface[] };

            onChange(prev => ({
                ...prev,
                promocodes: {
                    ...prev.promocodes,
                    data: prev.promocodes.data.map((p) => {
                        const updated = promocodesList.data.find((r) => r.id === p.id) || {};
                        return {
                            ...p,
                            foreignEvents: updated.foreignEvents || [],
                            tickets: updated.tickets || [],
                        };
                    })
                }
            }));
        } catch (err) {
            toast({
                type: 'danger',
                message: err.message || `${t('has_occurred')}. ${t('reload_page')}`
            });
        }
    }

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setLoading(prev => ({...prev, connecting: true}))
        const actions = prepareLinkActions();

        try {
            const requests = processPromocodeLinkActions(actions, data.promocodeId);
            const results = await Promise.allSettled(requests);

            const failedRequests = results.filter(result => result.status === 'rejected');
            if (failedRequests.length > 0) {
                console.error(`${t('query_error_result')}:`, failedRequests);
                toast({type: 'warning', message: `${t('query_error_result')}`});
            }

            await handleUpdatePromocode()

            onActive(false)
        } catch (err) {
            toast({type: 'danger', message: err.message || `${t('has_occurred')}. ${t('try_again_later')}`});
        } finally {
            setLoading(prev => ({...prev, connecting: false}))
        }
    }

    const isEventFullySelected = (event: EventBasesServiceInterface,): boolean => {
        const eventSelection = content.selectedEvents?.[event.eventId];
        if (!eventSelection) return false;

        const selectedTicketsCount = Object.keys(eventSelection).length;
        const allTicketsSelected = Object.values(eventSelection).every(Boolean);

        return allTicketsSelected && selectedTicketsCount === event.tickets.length;
    };

    const hasSelectedTickets = (event: EventBasesServiceInterface,): boolean => {
        const eventSelection = content.selectedEvents?.[event.eventId];
        return !!eventSelection && Object.values(eventSelection).some(Boolean);
    };

    const handlePage = async (value: number) => {
        setContent(prev => ({...prev, meta: {...prev.meta, currentPage: Number(value)}}))
    }

    const handleSize = async (value: number) => {
        setContent(prev => ({...prev, meta: {...prev.meta, perPage: Number(value)}}))
    }

    const isInitialPromocodeAttached = (eventId) => {
        return data.initialPromocodeEvent.map(promocode => Number(promocode.id)).includes(eventId)
    }

    return (
        <Modal
            active={active}
            onActive={onActive}
            className={styles.modal}
            onMissClick={console.log}
            onEscape={console.log}>
            <div className={styles.header}>
                <p>
                    Билеты
                </p>
                <form className={styles.search} onSubmit={handleSearchSubmit}>
                    <Input
                        value={content.search.value}
                        onChange={handleSearchChange}
                        size={'small'}
                        placeholder={t('search_by_name')}
                        disabled={isLoading.event || isLoading.connecting}
                    />
                    {!content.search.isActive
                        ? <Button
                            size={'small'}
                            theme={'ghost'}
                            type={'primary'}
                            htmlType={'submit'}
                            disabled={isLoading.event || isLoading.connecting}>
                            <SearchIcon/>
                        </Button>
                        : <Button
                            size={'small'}
                            theme={'ghost'}
                            type={'primary'}
                            onClick={handleSearchReset}
                            disabled={isLoading.event || isLoading.connecting}>
                            <CrossIcon/>
                        </Button>
                    }
                </form>
                <div className={styles.cross}>
                    <Button
                        size={'small'}
                        theme={'ghost'}
                        type={'tertiary'}
                        onClick={handleClose}>
                        <CrossIcon/>
                    </Button>
                </div>
            </div>
            {isLoading.event || isLoading.connecting
                ? <div className={styles.preloader}>
                    <Preloader/>
                    {isLoading.connecting ? <p className={styles.notFoundSearch}>{t('attach_events')}</p> : null}
                </div> : null
            }
            {(content.baseEvents?.length === 0 && !isLoading.event)
                ? <div className={styles.preloader}>
                    <p className={styles.notFoundSearch}>
                        {t('not_found_event')}
                    </p>
                </div> : null
            }
            {(!isLoading.event && !isLoading.connecting) && content.baseEvents?.length >= 1 ?
                <div className={styles.modalEvents}>
                    {content.baseEvents.map(event =>
                        <div key={event.id}>
                            <div className={styles.modalEvent}>
                                <div className={styles.eventCheckbox}>
                                    <Checkbox
                                        value={isEventFullySelected(event) || isInitialPromocodeAttached(event.eventId)}
                                        indeterminate={!isEventFullySelected(event) ? hasSelectedTickets(event) : false}
                                        onChange={(isChecked: boolean) => handleSelectEvent(event, isChecked)}
                                        size={'medium'}
                                    />
                                </div>
                                <div className={styles.info}>
                                    <p className={styles.infoTitle}>
                                        {event.eventTitle}
                                    </p>
                                    <div className={styles.eventStatus}>
                                        <Status
                                            type={EventStateSchema[event.state].type}
                                            data={null}
                                            onChange={() => null}
                                            size={'small'}>
                                            {EventStateSchema[event.state].value}
                                        </Status>
                                        <small>
                                            {event.dateWithTimezone}
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.modalTickets}>
                                {event.tickets ? event.tickets.map((ticket: TicketInterface) =>
                                    <div key={ticket.ticketId} className={styles.modalTicket}>
                                        <div>
                                            <Checkbox
                                                value={content?.selectedEvents && content.selectedEvents[event.eventId]?.[ticket.ticketId]}
                                                onChange={(isChecked: boolean) => handleSelectTicket(event.eventId, ticket.ticketId, isChecked)}
                                                size={'small'}
                                            />
                                        </div>
                                        <p>
                                            {ticket.name}
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    )}
                    {meta.total > 10 && <div className={styles.pagination}>
                        <Paginator
                            page={currentPage}
                            last={lastPage}
                            onPage={handlePage}
                            onSize={handleSize}
                            size={perPage}
                            sizes={sizes}
                            type={'secondary'}
                            theme={'ghost'}
                        />
                    </div>}
                </div> : null
            }
            <div className={styles.footer}>
                <div className={styles.selects}>
                    <Button
                        theme={'default'}
                        type={'secondary'}
                        htmlType={'button'}
                        disabled={isLoading.event || isLoading.connecting}
                        onClick={handleActive}>
                        {t('select_all')}
                    </Button>
                    <Button
                        theme={'default'}
                        type={'tertiary'}
                        htmlType={'button'}
                        disabled={isLoading.event || isLoading.connecting}
                        onClick={handleDrop}>
                        {t('unselect')}
                    </Button>
                </div>
                <div className={styles.submits}>
                    <Button
                        theme={'ghost'}
                        type={'secondary'}
                        htmlType={'button'}
                        disabled={isLoading.event || isLoading.connecting}
                        onClick={handleClose}>
                        {t('cancel')}
                    </Button>
                    <Button
                        theme={'default'}
                        type={'primary'}
                        htmlType={'button'}
                        disabled={isLoading.event || isLoading.connecting}
                        onClick={handleSubmit}>
                        {t('ready')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AttachEventsModal;
